// shared/src/prompts/p-contrarian-refine.ts
// Chain V2 — per-ticker contrarian refine. DEEP path only.
// Full re-emit (not DIFF). Consumes own analyst + own skeptic.
// Per design-notes L21.

import { z } from 'zod';
import { DirectionEnum, TickerAlignmentEnum, PredictionModeEnum } from '../resolvers/llm-enum-coerce.js';
import { llmNumber } from '../resolvers/llm-number-coerce.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import type { LlmNewsItem } from '../news/types.js';
import type { TickerMarketAnchor } from '../translators/market-emit-to-ticker-input.js';

// VERSION 1.2.0 (2026-05-25 §K.4): re-widen with 5 V1 fields the V2 narrowing
// dropped (catalyst, catalyst_date, catalyst_probability, invalidation_trigger,
// prediction_mode). The arbiter no longer derives these; f.1.finalize reads
// them directly from the winning refine. Per docs/2026-05-25-flex-schema-brief-revision.md §K.4.
// VERSION 1.3.0 (2026-06-11): schema rigidity hardening — change_pct/_ci + horizon_days
// + catalyst_probability llmNumber-coerced; mechanism/refinement_rationale/invalidation_trigger
// .min(1)→z.string() (cosmetic prose, ticker-emit defensive). docs/llm-schema-rigidity-hardening-2026-06-11.md.
export const VERSION = '1.3.0';
export const STEP    = 'p.contrarian.refine';
export const MODEL   = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 8000)
export const ROLE    = 'refine' as const;
export const DESC    = 'Re-emits the per-ticker contrarian call reconciled with the skeptic critique' as const;   // #45 registry — one-line operator description

export const OutputSchema = z.object({
  direction:            DirectionEnum,
  change_pct:           llmNumber(),
  change_ci_low:        llmNumber(),
  change_ci_high:       llmNumber(),
  horizon_days:         llmNumber(z.number().int().positive()),
  mechanism:            z.string(),     // 2026-06-11: .min(1) dropped (cosmetic prose)
  ticker_alignment:     TickerAlignmentEnum,
  is_headline:          z.boolean(),
  refinement_rationale: z.string(),     // 2026-06-11: .min(1) dropped (cosmetic audit-trail prose)
  // §K.4 re-widen — 5 fields f.1.finalize reads to populate the narrow signals contract.
  prediction_mode:      PredictionModeEnum,
  catalyst:             z.string(),                              // "" iff prediction_mode='trend'
  catalyst_date:        z.string().nullable(),                   // ISO 'YYYY-MM-DD' iff catalyst mode; null iff trend
  catalyst_probability: llmNumber(z.number().min(0).max(1).nullable()),     // null iff trend. 2026-06-11: llmNumber-coerced (range stays).
  invalidation_trigger: z.string(),                              // ≤30w price/condition that disproves. 2026-06-11: .min(1) dropped (cosmetic prose).
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# PER-TICKER CONTRARIAN REFINE (V2)

## ROLE
Refine the per-ticker CONTRARIAN analyst's draft given the contrarian skeptic critique. Full re-emit (not DIFF). Emit the PREDICTED STATE — never prescribe actions.

## REFINEMENT DISCIPLINE
- If skeptic surfaced factual_error: MANDATORY-fix (correct the targeted field).
- If skeptic surfaced reasonability OR opposing_draft cites: tighten the field OR address with explicit mechanism evidence.
- If skeptic conviction=LOW + no factual_error, refinement may be minimal (hold direction; adjust CI bounds if momentum/news drift).
- refinement_rationale MUST cite which skeptic point(s) drove each change.

## NO CALIBRATION. Reason FRESH from THIS brief's payload + skeptic + market anchor.

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary)
{
  "direction":            "up" | "down" | "flat",
  "change_pct":           <signed percent>,
  "change_ci_low":        <CI low>,
  "change_ci_high":       <CI high>,
  "horizon_days":         <int days>,
  "mechanism":            "<60-100w refined; predicted-state prose, no action verbs>",
  "ticker_alignment":     "aligns_market" | "diverges_market" | "neutral",
  "is_headline":          true | false,
  "refinement_rationale": "<1-2 sentences. Cite specific skeptic point(s) that drove changes. Audit trail.>",
  "prediction_mode":      "catalyst" | "trend",
  "catalyst":             "<≤30w named event + date when catalyst mode; \\"\\" when trend mode>",
  "catalyst_date":        "<ISO YYYY-MM-DD ≥ today when catalyst mode; null when trend mode>",
  "catalyst_probability": <0..1 when catalyst mode; null when trend mode>,
  "invalidation_trigger": "<≤30w price/condition that would falsify the predicted state>"
}

## RULES (strict)
1. First char='{'. Last char='}'. No commentary outside JSON.
2. Same DIRECTION/CHANGE_PCT/CI consistency as analyst: 'up' → change_pct > 0; 'down' → change_pct < 0; 'flat' → |change_pct| small.
3. CI BOUNDS contain change_pct: change_ci_low ≤ change_pct ≤ change_ci_high.
4. MODE RULES:
   - prediction_mode='catalyst' → catalyst (non-empty event prose) + catalyst_date (ISO ≥ today) + catalyst_probability (0..1) ALL REQUIRED.
   - prediction_mode='trend'    → catalyst="" AND catalyst_date=null AND catalyst_probability=null. horizon_days carries the window.
5. INVALIDATION_TRIGGER REQUIRED (non-empty). One sentence; price level OR specific event.
6. factual_error skeptic cites MANDATORY-fix.
7. refinement_rationale REQUIRED even when "skeptic conviction LOW; no material changes."
8. NO HEDGING. Pick direction + change_pct decisively.
9. NO ACTION VERBS in mechanism or refinement_rationale (no BUY/SELL/HOLD/TRIM/ADD/EXIT/ROTATE/CUT/REDUCE/TAKE-PROFIT/STOP-OUT). Describe the predicted STATE; the trader derives actions.
`;

export interface PContrarianRefineInput {
  readonly ticker:        string;
  readonly ticker_data:   Record<string, unknown>;
  readonly market_anchor: TickerMarketAnchor;
  readonly momentum:      Record<string, unknown>;
  readonly own_draft:     Record<string, unknown>;     // p.contrarian.analyst output
  readonly own_skeptic:   Record<string, unknown>;     // p.contrarian.skeptic output
  readonly news?:         ReadonlyArray<LlmNewsItem>;
  readonly grounding?:    WorldStateGrounding;
}

function fmt(v: unknown): string {
  if (v == null) return 'not set';
  if (typeof v === 'string') return v.length === 0 ? 'not set' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function formatSkeptic(s: Record<string, unknown> | undefined): string {
  if (!s || Object.keys(s).length === 0) return '  (no skeptic critique)';
  const ev = Array.isArray(s['disconfirming_evidence']) ? s['disconfirming_evidence'] : [];
  const evLines = ev.length > 0
    ? ev.map((e, i) => {
        const eo = e as Record<string, unknown>;
        return `    [${i + 1}] (${String(eo['cite'] ?? '?')}) ${String(eo['point'] ?? '?')}`;
      }).join('\n')
    : '    (none)';
  return `  conviction: ${fmt(s['conviction'])}
  reasoning:  ${fmt(s['reasoning'])}
  disconfirming_evidence:
${evLines}`;
}

function draftExcerpt(d: Record<string, unknown> | undefined): string {
  if (!d || Object.keys(d).length === 0) return '  (no draft)';
  return [
    `  direction:        ${fmt(d['direction'])}`,
    `  change_pct:       ${fmt(d['change_pct'])}`,
    `  change_ci_low:    ${fmt(d['change_ci_low'])}`,
    `  change_ci_high:   ${fmt(d['change_ci_high'])}`,
    `  horizon_days:     ${fmt(d['horizon_days'])}`,
    `  ticker_alignment: ${fmt(d['ticker_alignment'])}`,
    `  is_headline:      ${fmt(d['is_headline'])}`,
    `  mechanism:        ${fmt(d['mechanism'])}`,
  ].join('\n');
}

export function buildUserPrompt(input: PContrarianRefineInput): string {
  const groundingBlock = input.grounding ? renderWorldStateGrounding(input.grounding) + '\n' : '';
  const a = input.market_anchor;
  return `${groundingBlock}TICKER: ${input.ticker}

TICKER DATA:
${Object.entries(input.ticker_data).slice(0, 10).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)'}

MOMENTUM SIGNALS:
${Object.entries(input.momentum).slice(0, 10).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)'}

MARKET ANCHOR (m.synthesis primary):
  thesis_headline:  ${a.synthesis_anchor.thesis_headline}
  regime:           ${a.synthesis_anchor.regime}
  weights:          c=${a.synthesis_anchor.contrarian_weight} / co=${a.synthesis_anchor.consensus_weight}
  crux:             ${a.synthesis_anchor.crux}
contrarian boundary:
  ${a.contrarian_anchor.thesis_headline}

OWN_DRAFT (contrarian analyst — refine this):
${draftExcerpt(input.own_draft)}

OWN_SKEPTIC critique:
${formatSkeptic(input.own_skeptic)}

TICKER NEWS (last 48h):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_p_contrarian_refine

Emit a SINGLE JSON object matching the schema. FULL refined per-ticker thesis — not DIFF. factual_error skeptic cites MANDATORY-fix. refinement_rationale REQUIRED.`;
}
